// src/pages/Inventory/Inventory.js
/**
 * Inventory Dashboard (Receiver)
 * ------------------------------
 * Responsibilities:
 * - Fetch (or accept via props) inventory snapshot data: listings, requests, companies
 * - Enforce permissions for receiver inventory access
 * - Provide filter UX (draft vs applied) for date/material/period/aggregation
 * - Compute multiple dashboard "views" (base + per-trend-chart) via useInventoryDashboard
 * - Render:
 *    - Counters
 *    - Summary tables (Donors, Transactions)
 *    - Insights charts (donut + top bars)
 *    - Trend charts (lbs/pickups/cost) each with independent trend controls
 *    - A reception timeline view
 *
 * Key design decisions:
 * - Filters are split into "draft" and "applied" to avoid expensive recomputation on every keystroke.
 * - Trend charts each have their own breakdown/topN/series selection, stored under applied.trend.<metric>.
 * - Data joining is performed via Maps (listingById, usersById) for O(1) lookups.
 *
 * Maintainability notes:
 * - This file is mostly orchestration/glue. Heavy computation should remain inside hooks/utilities
 *   (useInventoryDashboard, useInventoryFiltersState, etc.).
 * - If you add another trend metric, follow the existing pattern: dashboardX + TrendControls + TrendLineChart.
 */

import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import {
  Box,
  Typography,
  OutlinedInput,
  Stack,
  Chip,
  Select,
  MenuItem,
} from "@mui/material";

import { useDarkMode } from "../../contexts/ThemeContext";
import { useTranslation } from "react-i18next";

import "../../style/InventoryDashboard.css";
import "../../style/ReceiverInventory.css";
import "../../style/mfca-dashboard.css";

// Page sections / components
import InventoryCounters from "../../components/Inventory/InventoryCounters";
import TransactionsTable from "../../components/Inventory/TransactionsTable";
import DonorsTable from "../../components/Inventory/DonorsTable";
import MaterialReceptionTimeline from "../../components/Inventory/MaterialReceptionTimeline";

// Data fetching + permissions
import { useInventorySnapshot } from "../../hooks/useInventorySnapshot.js";
import { usePermissionGuard } from "../../hooks/usePermissionGuard";

// UI helpers
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import Alert from "../../components/ui/Alert";
import LastUpdatedBadge from "../../components/ui/LastUpdatedBadge";

// Filters UI (component living under utils/ by name; it behaves like a component)
import InventoryFilters from "../../utils/InventoryFilters";

// Accordion + charts
import InventoryAccordion from "../../components/Inventory/InventoryAccordion";
import ReceiverMaterialsOverview from "../../components/Inventory/ReceiverMaterialsOverview";
import TopDonorsChart from "../../components/Inventory/charts/TopDonorsChart";
import HorizontalBarChart from "../../components/Inventory/charts/HorizontalBarChart";
import TrendLineChart from "../../components/Inventory/charts/TrendLineChart";

// Filter state + dashboard computation hooks
import useInventoryFiltersState from "../../hooks/useInventoryFiltersState";
import useInventoryDashboard from "../../hooks/useInventoryDashboard";

// Receiver MFCA — partial acceptance logging
import ReceiverRejectionInput from "../../components/ReceiverInventory/ReceiverRejectionInput";
import { useReceiverMFCAData } from "../../hooks/useReceiverMFCAData";
import { apiFetch } from "../../api/client";

/**
 * TrendControls
 * -------------
 * Pure UI component that renders the per-trend-chart control panel:
 * - Breakdown: overall | material | donor
 * - Top N: how many series to auto-pick (only relevant when breakdown != overall)
 * - Series selection: manual selection of specific materials/donors to compare
 *
 * Props:
 * - darkMode, t: styling + translations passed from parent (keeps this component dumb)
 * - selectMenuProps: shared MUI menu configuration for consistent positioning
 * - value: { breakdown, topN, seriesKeys }
 * - onBreakdown/onTopN/onSeriesKeys: parent handlers (usually write to filter state)
 * - seriesOptions: available series list, typically computed by useInventoryDashboard
 *
 * UX rules:
 * - If breakdown === "overall":
 *    - Top N and Series selection are disabled
 *    - renderValue shows "Total"
 * - If breakdown !== "overall":
 *    - If seriesKeys empty => "Auto (Top N)" indicates dashboard chooses top series
 *    - If seriesKeys present => show chips for the first few selections
 */
function TrendControls({
  darkMode,
  t,
  selectMenuProps,
  value, // { breakdown, topN, seriesKeys }
  onBreakdown,
  onTopN,
  onSeriesKeys,
  seriesOptions,
}) {
  /**
   * seriesLabel changes based on breakdown so the multi-select reads nicely.
   * - material => "Materials to Compare"
   * - donor    => "Companies/Donors to Compare"
   * - fallback => "Series"
   */
  const seriesLabel =
    value.breakdown === "material"
      ? t("Materials to Compare")
      : value.breakdown === "donor"
        ? t("Companies/Donors to Compare")
        : t("Series");

  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
        {/* Breakdown selector */}
        <Box sx={{ minWidth: 210 }}>
          <Typography variant="caption" className="inv-filter-label">
            {t("Trend Breakdown")}
          </Typography>

          <Select
            size="small"
            value={value.breakdown}
            onChange={(e) => onBreakdown(e.target.value)}
            MenuProps={selectMenuProps}
            // Keep text readable in dark mode
            sx={{
              width: "100%",
              "& .MuiSelect-select": { color: darkMode ? "#fff" : "#1a1a1a" },
            }}
          >
            <MenuItem value="overall">{t("Overall")}</MenuItem>
            <MenuItem value="material">{t("By Material")}</MenuItem>
            <MenuItem value="donor">{t("By Company/Donor")}</MenuItem>
          </Select>
        </Box>

        {/* Top N selector (only meaningful when breakdown !== overall) */}
        <Box sx={{ minWidth: 140 }}>
          <Typography variant="caption" className="inv-filter-label">
            {t("Top N")}
          </Typography>

          <Select
            size="small"
            value={value.topN}
            onChange={(e) => onTopN(Number(e.target.value))}
            MenuProps={selectMenuProps}
            sx={{
              width: "100%",
              "& .MuiSelect-select": { color: darkMode ? "#fff" : "#1a1a1a" },
            }}
            // Disable Top N when not applicable
            disabled={value.breakdown === "overall"}
          >
            {/* Deliberately curated values (avoid too many options / avoid 1-2 which are not very informative) */}
            {[3, 4, 5, 6, 8, 10].map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* Series multi-select (manual series choice) */}
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography variant="caption" className="inv-filter-label">
            {seriesLabel}
          </Typography>

          <Select
            multiple
            size="small"
            value={value.seriesKeys}
            onChange={(e) => onSeriesKeys(e.target.value)}
            input={<OutlinedInput />}
            displayEmpty
            MenuProps={selectMenuProps}
            disabled={value.breakdown === "overall"}
            /**
             * renderValue controls what appears inside the Select when closed.
             * We show:
             * - "Total" when overall
             * - "Auto (Top N)" when non-overall but no manual series chosen
             * - Chips of selected series otherwise
             */
            renderValue={(selected) =>
              value.breakdown === "overall" ? (
                <span className="inv-filter-placeholder">{t("Total")}</span>
              ) : selected.length === 0 ? (
                <span className="inv-filter-placeholder">
                  {t("Auto (Top N)")}
                </span>
              ) : (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.slice(0, 4).map((k) => {
                    // Translate key => label via seriesOptions; fallback to raw key
                    const label =
                      (seriesOptions || []).find((o) => o.key === k)?.label ||
                      String(k);
                    return <Chip key={String(k)} label={label} size="small" />;
                  })}
                  {selected.length > 4 ? (
                    <Chip label={`+${selected.length - 4}`} size="small" />
                  ) : null}
                </Box>
              )
            }
            sx={{
              width: "100%",
              "& .MuiSelect-select": { color: darkMode ? "#fff" : "#1a1a1a" },
            }}
          >
            {/* Options provided by dashboard hook (depends on filters + breakdown mode) */}
            {(seriesOptions || []).map((opt) => (
              <MenuItem key={opt.key} value={opt.key}>
                {opt.label}
              </MenuItem>
            ))}

            {/* Informative empty-state when breakdown supports series but none exist for the selected range */}
            {value.breakdown !== "overall" &&
            (seriesOptions || []).length === 0 ? (
              <MenuItem disabled value="">
                {t("No series available for this range")}
              </MenuItem>
            ) : null}
          </Select>
        </Box>
      </Stack>
    </Box>
  );
}

/**
 * Inventory (default export)
 * --------------------------
 * This is the full inventory dashboard page component.
 *
 * Props:
 * - listingsProp/requestsProp: optional external data injection (e.g., tests, storybooks)
 * - ...TableProps: customization hooks passed down to child components
 *
 * Data sources:
 * - Primary: useInventorySnapshot() returns { listings, requests, companies }
 * - Fallback: props if hook data isn't present
 * - Demo mode: query param ?demo=1 switches to demo state (currently empty placeholders)
 *
 * Filters:
 * - useInventoryFiltersState() provides draft + applied filter state, plus handlers.
 * - applied filters drive all dashboards and rendered components.
 * - draft filters are edited in the UI and only applied when the user clicks Apply.
 */
export default function Inventory({
  listings: listingsProp = [],
  requests: requestsProp = [],
  transactionsTableProps = {},
  donorsTableProps = {},
  materialReceptionTimelineProps = {},
  inventoryCountersProps = {},
}) {
  const { darkMode } = useDarkMode();
  const { t } = useTranslation();

  /**
   * Permission guard:
   * - isChecking: we show a loading state while permissions are being validated
   * - hasPermission: if false, the hook redirects to /no-permission and we render null as a safe fallback
   */
  const { hasPermission, isChecking } = usePermissionGuard(
    "receiver",
    "inventory",
  );

  /**
   * isVisible:
   * - UI animation/transition helper. We delay adding "visible" class until shortly after loading finishes.
   * - Keeps the first paint smoother (prevents transitions from firing during loading).
   */
  const [isVisible, setIsVisible] = useState(false);

  // page entrance animation (matches donor pattern)
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setInitialLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  /**
   * Ref to the root container:
   * - Currently not used directly in this file, but may be useful for:
   *   - intersection observers
   *   - scroll restoration
   *   - measuring layout for charts
   */
  const inventoryContainerRef = useRef(null);

  /**
   * demoOnly:
   * - Derived once from location search params.
   * - Wrapped in try/catch to avoid crashing in non-browser environments (tests/SSR).
   */
  const demoOnly = useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).get("demo") === "1";
    } catch {
      return false;
    }
  }, []);

  /**
   * Demo state placeholders (preserved for compatibility).
   * If demo mode is expanded later, these are the state variables to populate.
   */
  const [demoLoading] = useState(false);
  const [demoError] = useState("");
  const [demoListings] = useState([]);
  const [demoRequests] = useState([]);
  const [demoCompanies] = useState([]);

  /**
   * Snapshot fetch:
   * - data: { listings, requests, companies } (shape expected)
   * - loading/error: request lifecycle
   */
  const { data, loading, error, lastUpdated, performRefresh } =
    useInventorySnapshot();

  /**
   * Effective loading/error:
   * - When demoOnly is true, use demo placeholders
   * - Otherwise use real hook state
   */
  const pageLoading = demoOnly ? demoLoading : loading;
  const pageError = demoOnly ? demoError : error;

  /**
   * Visibility transition trigger:
   * - Only runs when loading finishes
   * - Adds a small delay so the DOM is mounted before CSS transitions run
   */
  useEffect(() => {
    if (!initialLoading && !pageLoading) {
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    }
  }, [initialLoading, pageLoading]);

  // badge refresh — local state controls spinner, not hook loading
  const [badgeRefreshing, setBadgeRefreshing] = useState(false);

  // counter card slide-up animation (effect below, after refreshing is destructured)
  const [justUpdated, setJustUpdated] = useState(false);
  const prevRefreshingRef = useRef(false);

  const handleRefresh = useCallback(async () => {
    setBadgeRefreshing(true);
    try {
      await performRefresh();
    } catch (e) {
      console.error("refresh error:", e);
    }
    setTimeout(() => setBadgeRefreshing(false), 2000);
  }, [performRefresh]);

  // receiver MFCA — partial acceptance modal
  // the modal owns submitting/saved/error state internally. the page
  // applies an in-memory overlay on top of `data.requests` so the table
  // reflects saved changes without a refetch (refetches show a full-page
  // spinner and disrupt the save → review flow).
  const [rejectionRow, setRejectionRow] = useState(null);
  const [rejectionOverrides, setRejectionOverrides] = useState({});
  const lastPayloadRef = useRef(null);

  const handleLogPartial = useCallback((row) => {
    setRejectionRow(row);
  }, []);

  const handleRejectionSubmit = useCallback(
    async (payload) => {
      if (!rejectionRow) return;
      const res = await apiFetch(
        `/allocate/requests/${rejectionRow.id}/acceptance`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to log acceptance");
      }
      // stash the payload so onSaved can merge without needing a refetch
      lastPayloadRef.current = payload;
    },
    [rejectionRow],
  );

  // ref-indirection for refreshMfca: the MFCA hook is initialized later in the
  // render body, and handleRejectionSaved (defined here) can't reference it
  // directly without a temporal dead zone error in the useCallback deps.
  const refreshMfcaRef = useRef(() => {});

  // called by modal once the save succeeds. merge into the local overlay so
  // the row reflects the new state immediately without refetching the snapshot.
  // also triggers an MFCA refresh so the summary / tables recompute against the
  // updated acceptance data.
  const handleRejectionSaved = useCallback(() => {
    if (!rejectionRow || !lastPayloadRef.current) return;
    const p = lastPayloadRef.current;
    const wasFirstLog = rejectionRow.firstLoggedAt == null;
    const nowIso = new Date().toISOString();
    setRejectionOverrides((o) => ({
      ...o,
      [rejectionRow.id]: {
        quantity_accepted: p.quantity_accepted,
        rejection_reason: p.rejection_reason,
        rejection_disposition: p.rejection_disposition,
        rejection_notes: p.notes,
        date_modified: nowIso,
        ...(wasFirstLog ? { first_logged_at: nowIso } : {}),
      },
    }));
    lastPayloadRef.current = null;
    refreshMfcaRef.current();
  }, [rejectionRow]);

  const handleRejectionClose = useCallback(() => {
    setRejectionRow(null);
  }, []);

  /**
   * listings:
   * - In demo mode, use demoListings
   * - Otherwise prefer hook data if available and valid
   * - Otherwise fall back to listingsProp passed in
   *
   * This logic allows:
   * - normal operation via snapshot hook
   * - testing/storybook injection via props
   */
  const listings = useMemo(() => {
    if (demoOnly) return demoListings;
    return Array.isArray(data?.listings) ? data.listings : listingsProp;
  }, [demoOnly, demoListings, data, listingsProp]);

  /**
   * requests:
   * Same pattern as listings.
   * Additionally merges `rejectionOverrides` so a just-logged partial acceptance
   * shows up immediately without a snapshot refetch.
   */
  const requests = useMemo(() => {
    const base = demoOnly
      ? demoRequests
      : Array.isArray(data?.requests)
        ? data.requests
        : requestsProp;
    if (!base || !Object.keys(rejectionOverrides).length) return base;
    return base.map((r) => {
      const o = rejectionOverrides[r.id];
      return o ? { ...r, ...o } : r;
    });
  }, [demoOnly, demoRequests, data, requestsProp, rejectionOverrides]);

  /**
   * effectiveCompanies:
   * - Companies/users list used for donor labeling and series options
   * - In non-demo mode, only uses hook data; there is no prop fallback here by design.
   */
  const effectiveCompanies = useMemo(() => {
    if (demoOnly) return demoCompanies;
    return Array.isArray(data?.companies) ? data.companies : [];
  }, [demoOnly, demoCompanies, data]);

  /**
   * usersById:
   * - Map<string, user/company> for fast lookups
   * - Normalizes id to string to prevent type mismatch between numeric/string IDs
   */
  const usersById = useMemo(() => {
    const m = new Map();
    (effectiveCompanies || []).forEach((u) => {
      if (u?.id != null) m.set(String(u.id), u);
    });
    return m;
  }, [effectiveCompanies]);

  /**
   * listingById:
   * - Map<string, listing> for fast lookups from request.listing_id
   */
  const listingById = useMemo(() => {
    const map = new Map();
    (listings || []).forEach((l) => map.set(String(l.id), l));
    return map;
  }, [listings]);

  /**
   * materialOptions:
   * - Builds a unique, sorted list of material names for filter dropdowns.
   * - Only includes materials where the request has been delivered (date_delivered exists),
   *   so users filter against actually received inventory.
   *
   * Material field fallbacks:
   * - listing.material
   * - listing.materialType
   * - listing.material_name
   * - listing.material_type
   *
   * NOTE:
   * - If you change listing schema, update this extraction list.
   */
  const materialOptions = useMemo(() => {
    const set = new Set();
    (requests || []).forEach((r) => {
      if (!r?.date_delivered) return;

      const listing = listingById.get(String(r.listing_id));
      const mat =
        listing?.material ||
        listing?.materialType ||
        listing?.material_name ||
        listing?.material_type;

      if (mat) set.add(mat);
    });

    // Sort for predictable UI ordering
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [listingById, requests]);

  /**
   * Filters state (draft + applied)
   * -------------------------------
   * applied: values currently driving the dashboard (safe to recompute expensive things)
   * draft: setters + draft values for UI editing
   * isModified: whether draft differs from applied
   * refreshing: whether dashboard is recomputing / applying filters (UX: show dim/overlay)
   * handleReset: reset draft back to applied/defaults
   * handleApply: commit draft => applied
   * setters: specialized setters for nested filter fields (including trend controls)
   */
  const {
    applied,
    draft,
    isModified,
    refreshing,
    handleReset,
    handleApply,
    seedRange,
    setters,
  } = useInventoryFiltersState();

  // seed filter date range from actual delivered data
  useEffect(() => {
    if (!requests?.length) return;
    let min = null,
      max = null;
    for (const r of requests) {
      const d = r.date_delivered;
      if (!d) continue;
      const s = String(d).slice(0, 10);
      if (!min || s < min) min = s;
      if (!max || s > max) max = s;
    }
    if (min && max) seedRange(min, max);
  }, [requests, seedRange]);

  // counter card slide-up on refresh complete
  useEffect(() => {
    if (prevRefreshingRef.current && !refreshing) {
      setJustUpdated(true);
      const timer = setTimeout(() => setJustUpdated(false), 500);
      return () => clearTimeout(timer);
    }
    prevRefreshingRef.current = refreshing;
  }, [refreshing]);

  /**
   * dashboardBase
   * -------------
   * Base dashboard computations used by:
   * - donut (material mix)
   * - top donors bar chart
   * - top materials bar chart
   *
   * IMPORTANT:
   * - Trend parameters do not matter for these charts; we pin them to "overall"/defaults.
   * - This prevents unnecessary recompute sensitivity to trend controls.
   */
  const dashboardBase = useInventoryDashboard({
    requests,
    listingById,
    usersById,
    dateFrom: applied.dateFrom,
    dateTo: applied.dateTo,
    selectedMaterials: applied.selectedMaterials,
    period: applied.period,
    trendBreakdown: "overall",
    trendSeriesKeys: [],
    trendTopN: 6,
  });

  /**
   * Per-trend metric dashboards
   * ---------------------------
   * Each trend chart has independent controls stored under applied.trend.<metric>.
   * We compute separate dashboards so:
   * - seriesOptions can reflect the metric context
   * - trend time series can be computed appropriately
   */
  const dashboardLbs = useInventoryDashboard({
    requests,
    listingById,
    usersById,
    dateFrom: applied.dateFrom,
    dateTo: applied.dateTo,
    selectedMaterials: applied.selectedMaterials,
    period: applied.period,
    trendBreakdown: applied.trend?.lbs?.breakdown || "overall",
    trendSeriesKeys: applied.trend?.lbs?.seriesKeys || [],
    trendTopN: applied.trend?.lbs?.topN ?? 6,
  });

  const dashboardPickups = useInventoryDashboard({
    requests,
    listingById,
    usersById,
    dateFrom: applied.dateFrom,
    dateTo: applied.dateTo,
    selectedMaterials: applied.selectedMaterials,
    period: applied.period,
    trendBreakdown: applied.trend?.pickups?.breakdown || "overall",
    trendSeriesKeys: applied.trend?.pickups?.seriesKeys || [],
    trendTopN: applied.trend?.pickups?.topN ?? 6,
  });

  const dashboardCost = useInventoryDashboard({
    requests,
    listingById,
    usersById,
    dateFrom: applied.dateFrom,
    dateTo: applied.dateTo,
    selectedMaterials: applied.selectedMaterials,
    period: applied.period,
    trendBreakdown: applied.trend?.cost?.breakdown || "overall",
    trendSeriesKeys: applied.trend?.cost?.seriesKeys || [],
    trendTopN: applied.trend?.cost?.topN ?? 6,
  });

  // mfca summary widget — uses last-90-days as a fixed range, intentionally
  // independent of the page's own filter state. the full filterable view lives
  // on the Flow Analysis tab (/receiver/flow-analysis). cache keys are
  // shared with the Flow Analysis page's hook when filters happen to match
  // — that's a feature: same data, instant paint on first navigation.
  const inventoryMfcaRange = useMemo(() => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 90);
    return {
      dateFrom: from.toISOString().slice(0, 10),
      dateTo: to.toISOString().slice(0, 10),
    };
  }, []);
  // mfca hook stays even though the summary widget is gone — handleRejectionSaved
  // calls refreshMfca so the Flow Analysis page gets fresh data when the user
  // navigates there after editing acceptance on a row here. cache is shared
  // across both pages via DataCacheContext.
  const { refresh: refreshMfca } = useReceiverMFCAData({
    dateFrom: inventoryMfcaRange.dateFrom,
    dateTo: inventoryMfcaRange.dateTo,
    materials: [],
    enabled: !demoOnly,
  });

  // keep the ref (used by handleRejectionSaved) pointed at the live refresh fn
  useEffect(() => {
    refreshMfcaRef.current = refreshMfca;
  }, [refreshMfca]);

  /**
   * selectMenuProps:
   * - Shared MUI Select menu positioning configuration to avoid popover jitter.
   * - Memoized to keep stable object identity (prevents rerenders and popover reset).
   */
  const selectMenuProps = useMemo(
    () => ({
      disablePortal: false,
      anchorOrigin: { vertical: "bottom", horizontal: "left" },
      transformOrigin: { vertical: "top", horizontal: "left" },
    }),
    [],
  );

  /**
   * Render guards:
   * 1) permissions checking spinner
   * 2) deny (null)
   * 3) loading spinner
   * After that, render the full dashboard.
   */
  if (isChecking) {
    return (
      <LoadingSpinner
        fullPage
        size={50}
        message={t("Checking permissions...")}
      />
    );
  }

  if (!hasPermission) {
    return null;
  }

  if (initialLoading || pageLoading) {
    return (
      <LoadingSpinner fullPage size={50} message={t("Fetching inventory...")} />
    );
  }

  /**
   * Main render
   * -----------
   * Structure:
   * - Optional page-level error banner
   * - Root container with theme + visibility classes
   * - Filters
   * - Counters
   * - Accordion sections:
   *    - Summary (Donors + Transactions)
   *    - Insights (donut + top charts)
   *    - Trends (3 independent trend charts with controls)
   * - Reception timeline at bottom
   */
  return (
    <>
      {/* Page-level error banner (non-blocking: still shows UI below if desired) */}
      {pageError ? (
        <Box className="inv-content-wrapper" sx={{ mb: 3 }}>
          <Alert message={pageError} severity="error" />
        </Box>
      ) : null}

      {/* Root container: theme class + visibility class for transitions */}
      <Box
        ref={inventoryContainerRef}
        className={`material-management-root inventory-root inventory-dashboard-container ${
          darkMode ? "dark" : "light"
        } ${isVisible ? "visible" : ""}`}
      >
        <Box className="inv-inner" sx={{ px: { xs: 2, sm: 3 }, pb: 4 }}>
          <div className="page-header-row">
            <div />
            <LastUpdatedBadge
              timestamp={lastUpdated}
              onRefresh={handleRefresh}
              isRefreshing={badgeRefreshing}
              variant="receiver"
            />
          </div>

          {/* Filters */}
          <InventoryFilters
            darkMode={darkMode}
            t={t}
            draftDateFrom={draft.draftDateFrom}
            setDraftDateFrom={draft.setDraftDateFrom}
            draftDateTo={draft.draftDateTo}
            setDraftDateTo={draft.setDraftDateTo}
            draftPeriod={draft.draftPeriod}
            setDraftPeriod={draft.setDraftPeriod}
            draftSelectedMaterials={draft.draftSelectedMaterials}
            setDraftSelectedMaterials={draft.setDraftSelectedMaterials}
            materialOptions={materialOptions}
            isModified={isModified}
            refreshing={refreshing}
            onReset={handleReset}
            onApply={handleApply}
          />

          {/* Counters summary
              - Uses applied filters directly, so it reflects the currently committed state.
              - refreshing class can dim/indicate recalculation. */}
          <Box
            className={`inv-counters-wrapper ${refreshing ? "is-refreshing" : ""}`}
            sx={{ mb: 3 }}
          >
            <Box className="inv-counters-grid" sx={{ gap: "18px" }}>
              <InventoryCounters
                darkMode={darkMode}
                listings={listings}
                requests={requests}
                dateFrom={applied.dateFrom}
                dateTo={applied.dateTo}
                materials={applied.selectedMaterials}
                isLoading={refreshing}
                justUpdated={justUpdated}
                {...inventoryCountersProps}
              />
            </Box>
          </Box>

          {/* Content: stacked sections/accordions */}
          <Box
            className={`inv-content-wrapper ${refreshing ? "is-refreshing" : ""}`}
            sx={{ display: "flex", flexDirection: "column", gap: "32px" }}
          >
            {/* SUMMARY: Donor and transaction tables */}
            <InventoryAccordion
              id="inv-summary"
              title={t("Allocation Summary")}
            >
              <Stack spacing={4}>
                {/* Donor-level totals/breakdown */}
                <DonorsTable
                  listings={listings}
                  requests={requests}
                  dateFrom={applied.dateFrom}
                  dateTo={applied.dateTo}
                  materials={applied.selectedMaterials}
                  {...donorsTableProps}
                />

                {/* Transactions table: wrapped in a scroll container to avoid huge page height */}
                <Box
                  sx={{
                    maxHeight: 420,
                    overflowY: "auto",
                    pr: 1,
                    borderRadius: 2,
                    // Custom scrollbars (WebKit). Keep purely cosmetic.
                    "&::-webkit-scrollbar": { width: 10 },
                    "&::-webkit-scrollbar-thumb": {
                      background: darkMode
                        ? "rgba(255,255,255,0.18)"
                        : "rgba(0,0,0,0.18)",
                      borderRadius: 10,
                    },
                    "&::-webkit-scrollbar-track": {
                      background: darkMode
                        ? "rgba(255,255,255,0.06)"
                        : "rgba(0,0,0,0.06)",
                      borderRadius: 10,
                    },
                  }}
                >
                  <TransactionsTable
                    dateFrom={applied.dateFrom}
                    dateTo={applied.dateTo}
                    period={applied.period}
                    materials={applied.selectedMaterials}
                    aggregated={applied.aggregated}
                    listings={listings}
                    requests={requests}
                    onLogPartial={handleLogPartial}
                    {...transactionsTableProps}
                  />
                </Box>
              </Stack>
            </InventoryAccordion>

            {/* INSIGHTS: Material mix + top donors/materials */}
            <InventoryAccordion
              id="inv-insights"
              title={t("Insights & Charts")}
              defaultExpanded
            >
              <ReceiverMaterialsOverview
                pieData={dashboardBase.pie.slice(0, 10)}
                requests={requests}
                listingById={listingById}
                usersById={usersById}
                dateFrom={applied.dateFrom}
                dateTo={applied.dateTo}
                selectedMaterials={applied.selectedMaterials}
                darkMode={darkMode}
              />

              <Box sx={{ mt: 3 }}>
                <TopDonorsChart
                  topDonors={dashboardBase.topDonors}
                  requests={requests}
                  listingById={listingById}
                  materialOptions={materialOptions}
                  darkMode={darkMode}
                />
              </Box>

              <Box sx={{ mt: 3 }}>
                <HorizontalBarChart
                  data={dashboardBase.topMaterials}
                  darkMode={darkMode}
                  title={t("Top Materials")}
                  subtitle={t("Most received materials (lbs)")}
                  minHeight={340}
                  chartKey={`mats__${applied.dateFrom}__${applied.dateTo}__${applied.period}`}
                />
              </Box>
            </InventoryAccordion>

            {/* TRENDS: multiple independent time series charts */}
            <InventoryAccordion id="inv-trends" title={t("Trends & History")}>
              <Stack spacing={4}>
                {/* Trend: LBS received */}
                <Box>
                  <TrendControls
                    darkMode={darkMode}
                    t={t}
                    selectMenuProps={selectMenuProps}
                    value={applied.trend.lbs}
                    /**
                     * When breakdown changes:
                     * - update breakdown
                     * - clear series selection so dashboard can re-auto-select or allow new manual selection
                     */
                    onBreakdown={(v) => {
                      setters.setTrendBreakdown("lbs", v);
                      setters.setTrendSeriesKeys("lbs", []);
                    }}
                    onTopN={(n) => setters.setTrendTopN("lbs", n)}
                    onSeriesKeys={(keys) =>
                      setters.setTrendSeriesKeys("lbs", keys)
                    }
                    seriesOptions={dashboardLbs.seriesOptions}
                  />

                  <TrendLineChart
                    /**
                     * Data source depends on breakdown mode:
                     * - overall: single total line (dashboardLbs.trend)
                     * - by series: multiple lines (dashboardLbs.trendBySeries)
                     */
                    data={
                      applied.trend.lbs.breakdown === "overall"
                        ? dashboardLbs.trend
                        : dashboardLbs.trendBySeries
                    }
                    /**
                     * series array likely controls legend/order/colors for multi-series mode.
                     * In overall mode we pass [] to indicate single series.
                     */
                    series={
                      applied.trend.lbs.breakdown === "overall"
                        ? []
                        : dashboardLbs.activeSeries
                    }
                    darkMode={darkMode}
                    title={t("Reception Trend")}
                    subtitle={
                      applied.trend.lbs.breakdown === "overall"
                        ? t("Total lbs received over time")
                        : t("Compare lbs received over time")
                    }
                    height={360}
                    unit="lbs"
                    period={applied.period}
                    chartKey={`trend__lbs__${applied.dateFrom}__${applied.dateTo}__${applied.period}__${applied.trend.lbs.breakdown}`}
                  />
                </Box>

                {/* Trend: Pickups (count of delivered pickups over time) */}
                <Box>
                  <TrendControls
                    darkMode={darkMode}
                    t={t}
                    selectMenuProps={selectMenuProps}
                    value={applied.trend.pickups}
                    onBreakdown={(v) => {
                      setters.setTrendBreakdown("pickups", v);
                      setters.setTrendSeriesKeys("pickups", []);
                    }}
                    onTopN={(n) => setters.setTrendTopN("pickups", n)}
                    onSeriesKeys={(keys) =>
                      setters.setTrendSeriesKeys("pickups", keys)
                    }
                    seriesOptions={dashboardPickups.seriesOptions}
                  />

                  <TrendLineChart
                    data={
                      applied.trend.pickups.breakdown === "overall"
                        ? dashboardPickups.pickupTrend
                        : dashboardPickups.pickupTrendBySeries
                    }
                    series={
                      applied.trend.pickups.breakdown === "overall"
                        ? []
                        : dashboardPickups.activeSeries
                    }
                    darkMode={darkMode}
                    title={t("Pickups Over Time")}
                    subtitle={
                      applied.trend.pickups.breakdown === "overall"
                        ? t("Number of delivered pickups over time")
                        : t("Compare delivered pickups over time")
                    }
                    height={320}
                    unit={t("pickups")}
                    period={applied.period}
                    chartKey={`trend__pickups__${applied.dateFrom}__${applied.dateTo}__${applied.period}__${applied.trend.pickups.breakdown}`}
                  />
                </Box>

                {/* Trend: Cost over time */}
                <Box>
                  <TrendControls
                    darkMode={darkMode}
                    t={t}
                    selectMenuProps={selectMenuProps}
                    value={applied.trend.cost}
                    onBreakdown={(v) => {
                      setters.setTrendBreakdown("cost", v);
                      setters.setTrendSeriesKeys("cost", []);
                    }}
                    onTopN={(n) => setters.setTrendTopN("cost", n)}
                    onSeriesKeys={(keys) =>
                      setters.setTrendSeriesKeys("cost", keys)
                    }
                    seriesOptions={dashboardCost.seriesOptions}
                  />

                  <TrendLineChart
                    data={
                      applied.trend.cost.breakdown === "overall"
                        ? dashboardCost.costTrend
                        : dashboardCost.costTrendBySeries
                    }
                    series={
                      applied.trend.cost.breakdown === "overall"
                        ? []
                        : dashboardCost.activeSeries
                    }
                    darkMode={darkMode}
                    title={t("Cost Over Time")}
                    subtitle={
                      applied.trend.cost.breakdown === "overall"
                        ? t("Total cost over time")
                        : t("Compare cost over time")
                    }
                    height={320}
                    unit="$"
                    period={applied.period}
                    chartKey={`trend__cost__${applied.dateFrom}__${applied.dateTo}__${applied.period}__${applied.trend.cost.breakdown}`}
                  />
                </Box>
              </Stack>
            </InventoryAccordion>

            {/* Reception timeline
                - Scroll container to cap height (this can get long)
                - Uses applied filters and aggregation settings
             */}
            <Box sx={{ maxHeight: 560, overflowY: "auto", pr: 1 }}>
              <MaterialReceptionTimeline
                dateFrom={applied.dateFrom}
                dateTo={applied.dateTo}
                period={applied.period}
                aggregated={applied.aggregated}
                materials={applied.selectedMaterials}
                listings={listings}
                requests={requests}
                {...materialReceptionTimelineProps}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* receiver MFCA — partial acceptance logging */}
      {rejectionRow && (
        <ReceiverRejectionInput
          key={rejectionRow.id}
          open={!!rejectionRow}
          onClose={handleRejectionClose}
          onSubmit={handleRejectionSubmit}
          onSaved={handleRejectionSaved}
          darkMode={darkMode}
          deliveredQty={rejectionRow.weight}
          donor={rejectionRow.donor}
          material={rejectionRow.material}
          initialAccepted={rejectionRow.quantityAccepted}
          initialReason={rejectionRow.rejectionReason}
          initialNotes={rejectionRow.rejectionNotes}
          firstLoggedAt={rejectionRow.firstLoggedAt}
          dateModified={rejectionRow.dateModified}
        />
      )}
    </>
  );
}
